import { RefObject, useEffect, useRef } from 'react';

// Shared dismiss behavior for the page's WAI-ARIA menu buttons (header Export,
// node panel Actor profile, Actors-tab row kebab). While open, the menu closes
// on: outside pointerdown, Escape (consumed with preventDefault so it never
// also closes an enclosing graph overlay; refocuses the trigger), and focus
// leaving the wrapper (Tab-out). On open, focus moves to the first enabled
// menu item so arrow-key navigation works immediately.
export function useMenuDismiss(
  open: boolean,
  wrapRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  close: () => void,
): void {
  // `close` goes through a ref so an inline arrow at the call site doesn't
  // re-run the effect (and re-focus the first item) on every render
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return undefined;
    const wrap = wrapRef.current;
    const handlePointerDown = (event: PointerEvent) => {
      if (!wrap?.contains(event.target as Node)) closeRef.current();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        triggerRef.current?.focus();
      }
    };
    const handleFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      // relatedTarget is null when focus moves to the body (e.g. canvas click) —
      // the pointerdown listener covers that case
      if (next && !wrap?.contains(next)) closeRef.current();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    wrap?.addEventListener('focusout', handleFocusOut);
    wrap?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      wrap?.removeEventListener('focusout', handleFocusOut);
    };
    // wrapRef/triggerRef are stable ref objects; only `open` drives the lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
